import { Button, Card, Input, Modal, Space, Table, Tag, Select } from "antd";
import { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { t } from "i18next";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import { useUser, PERMISSIONS } from "../../core/context/UserContext";
import UserCreateEdit from "./UserCreateEdit";
import usePermissionGuard from "../role/usePermissionGuard";

const { Option } = Select;

// Kullanıcı listesi az kayıtlı ve sunucudan tek seferde geliyor; arama/sıralama tarayıcıda yapılır.
const matches = (user, term, locale) => {
    const needle = term.toLocaleUpperCase(locale);
    return [user?.name, user?.username, user?.email, user?.role?.name]
        .some(value => (value ?? '').toLocaleUpperCase(locale).includes(needle));
};

// Kasa panosu izni kullanıcıya doğrudan ya da rolü üzerinden verilmiş olabilir; ikisine de bakılır.
const hasCashDashboard = (user) =>
    (user?.permissionIdList ?? []).includes(PERMISSIONS.CASH_DASHBOARD) ||
    (user?.role?.permissionIdList ?? []).includes(PERMISSIONS.CASH_DASHBOARD);

export default function UserList() {
    const history = useHistory();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const { firmSettings } = useUser();
    const { canGrant, canManageUser } = usePermissionGuard();

    const [enableFilter, setEnableFilter] = useState(true);
    const [searchText, setSearchText] = useState("");

    // Düzenleme ayrı sayfaya gitmeden modalda açılır; liste, filtre ve sıralama olduğu yerde kalır.
    const [editUserId, setEditUserId] = useState(null);

    const getUsers = async (filter = {}) => {
        const res = await ApiService.User.GetUserList(filter);
        if (res.success) {
            setUsers(res.data);
        }
    };

    const getRoles = async () => {
        const res = await ApiService.Role.GetRoleList();
        if (res.success) {
            setRoles(res.data);
        }
    };

    useEffect(() => {
        getUsers({ Enable: enableFilter });
        getRoles();
    }, []);

    const filteredUsers = useMemo(() => {
        if (!searchText) return users;
        return users.filter(u => matches(u, searchText, firmSettings?.locale));
    }, [users, searchText, firmSettings?.locale]);

    // Satır içi rol değişimi: kullanıcının firma, kasa, ek izin ve parola alanlarına dokunmaz.
    const changeRole = async (row, roleId) => {
        const res = await ApiService.User.ChangeUserRole(row.id, roleId);
        if (res.success) {
            setUsers(users.map(u => u.id === row.id
                ? { ...u, role: roles.find(r => r.id === roleId) ?? u.role }
                : u));
        }
    };

    const compare = (a, b) => (a ?? '').localeCompare(b ?? '', firmSettings?.locale);

    const renderAction = (row) => (
        <Space>
            <a onClick={() => setEditUserId(row.id)}>{t("Edit")}</a>
        </Space>
    );

    const editingUser = users.find(x => x.id === editUserId);

    return (
        <SimpleLayout fullWidth={true}>
            <Card>
                <Space style={{ marginBottom: 16 }}>
                    <Button
                        size={"small"}
                        type={"primary"}
                        onClick={() => history.push(`/app/system/users/create`)}
                    >
                        {t("New")}
                    </Button>
                    <Button
                        size={"small"}
                        onClick={() => getUsers({ Enable: enableFilter })}
                    >
                        {t("Refresh")}
                    </Button>
                    <Select
                        allowClear
                        size="small"
                        placeholder={t("Enable")}
                        style={{ width: 120 }}
                        value={enableFilter}
                        onChange={(value) => {
                            setEnableFilter(value);
                            getUsers({ Enable: value });
                        }}
                    >
                        <Option value={true}>{t("Active")}</Option>
                        <Option value={false}>{t("Passive")}</Option>
                    </Select>
                    <Input
                        size={"small"}
                        allowClear
                        placeholder={t("Search")}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </Space>

                <Table
                    size={"small"}
                    dataSource={filteredUsers}
                    pagination={false}
                    rowKey="id"
                    columns={[
                        {
                            key: "name",
                            dataIndex: "name",
                            title: t("Name"),
                            defaultSortOrder: "ascend",
                            sorter: (a, b) => compare(a.name, b.name),
                        },
                        {
                            key: "username",
                            dataIndex: "username",
                            title: t("Username"),
                            sorter: (a, b) => compare(a.username, b.username),
                        },
                        {
                            key: "email",
                            dataIndex: "email",
                            title: t("Email"),
                            sorter: (a, b) => compare(a.email, b.email),
                        },
                        {
                            key: "role",
                            dataIndex: "role",
                            title: t("Role"),
                            sorter: (a, b) => compare(a?.role?.name, b?.role?.name),
                            render: (_, row) => (
                                <Select
                                    size={"small"}
                                    style={{ width: 180 }}
                                    value={row?.role?.id}
                                    // Kendinden yetkili kullanıcının rolü değiştirilemez; kendinden yetkili rol verilemez
                                    disabled={!canManageUser(row)}
                                    options={roles.map(r => ({
                                        ...r,
                                        disabled: !canGrant([...(r.permissionIdList ?? []), ...(row.permissionIdList ?? [])])
                                    }))}
                                    fieldNames={{ label: 'name', value: 'id' }}
                                    onChange={(roleId) => changeRole(row, roleId)}
                                />
                            ),
                        },
                        {
                            key: "dashboard",
                            title: t("Dashboard"),
                            sorter: (a, b) => Number(hasCashDashboard(a)) - Number(hasCashDashboard(b)),
                            render: (_, row) => hasCashDashboard(row)
                                ? <Tag color={"blue"}>{t("Cash Dashboard")}</Tag>
                                : null,
                        },
                        { key: "#", dataIndex: "#", title: "#", render: (_, row) => renderAction(row) },
                    ]}
                />
            </Card>

            <Modal
                open={!!editUserId}
                title={`${t("Edit User")}${editingUser ? ` - ${editingUser.name}` : ''}`}
                width={800}
                footer={null}
                destroyOnClose={true}
                onCancel={() => setEditUserId(null)}
            >
                <UserCreateEdit
                    userId={editUserId}
                    embedded={true}
                    onSaved={() => {
                        setEditUserId(null);
                        getUsers({ Enable: enableFilter });
                    }}
                />
            </Modal>
        </SimpleLayout>
    );
}
